import { useContext } from "react";
import {
  useRouteContext,
  useNavigate,
  useParams,
  useLocation,
} from "@tanstack/react-router";
import SidebarMenu from "./SidebarMenu";
import { type SidebarItemProps, type SubMenuItemProps } from "./SidebarItem";
import QuickSearch from "./QuickSearch";
import { FileSubmissionsContext } from "@/components/layout/FileSubmissionsTracker";
import { useAddChild } from "@/components/layout/AddChildContext";
import { useCertifications } from "@/queries";
import { isAdminBuild } from "@/utils/isAdminBuild";

import {
  FiHome,
  FiList,
  FiUpload,
  FiDownload,
  FiFileText,
  FiAlertTriangle,
  FiCheckCircle,
  FiBarChart2,
  FiUsers,
  FiHelpCircle,
  FiMessageSquare,
  FiGrid,
  FiSettings,
} from "react-icons/fi";

export default function LeaSidebar() {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const { pendingFileSubmissions } = useContext(FileSubmissionsContext);
  const { openAddChildWizard, openChildTab } = useAddChild();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams({ from: "/$leaid" });
  const canFileUpload = leaContext.editFilePermissions().length > 0;
  const hasRole = leaContext.hasRole;
  const isStateLevel = params.leaid === "0-";

  const { data: certifications } = useCertifications(
    params.leaid,
    !isStateLevel && hasRole("ReportsEOY", "ReportsFall", "ReportsMonthly")
  );

  const sortedCerts = (certifications ?? [])
    .filter((cert) => cert.Status !== "Upcoming")
    .sort((a, b) => {
      const dateA = a.WindowStart
        ? new Date(a.WindowStart.split("T")[0] + "T00:00:00").getTime()
        : 0;
      const dateB = b.WindowStart
        ? new Date(b.WindowStart.split("T")[0] + "T00:00:00").getTime()
        : 0;
      return dateB - dateA;
    });

  const currentCycleId = sortedCerts[0]?.ReportingCalendarId
    ? String(sortedCerts[0].ReportingCalendarId)
    : null;

  const handleAddChild = () => {
    localStorage.setItem("openAddChildWizard", "true");
    navigate({ to: "/$leaid/records/child", params: { leaid: params.leaid } });
    setTimeout(() => openAddChildWizard(), 100);
  };

  const isOnChildPage = location.pathname.includes("/records/child");

  const handleMatchTool = () => {
    if (isOnChildPage) {
      openChildTab("match");
    } else {
      navigate({
        to: "/$leaid/records/child",
        params: { leaid: params.leaid },
        hash: "match",
      });
    }
  };

  const pendingCount = pendingFileSubmissions.length;

  const recordsSubItems: SubMenuItemProps[] = [
    {
      text: "Classroom Data",
      linkTo: "/$leaid/records/classroom",
      hasPermission: hasRole("CLRMView", "CLENView", "SCLRView"),
    },
    {
      text: "Staff Data",
      linkTo: "/$leaid/records/staff",
      hasPermission: hasRole("STFFView", "SASSView"),
    },
    {
      text: "Child Data",
      linkTo: "/$leaid/records/child",
      hasPermission: hasRole(
        "CHLDView",
        "FAMIView",
        "LANGView",
        "SLNGView",
        "ENRLView"
      ),
    },
    {
      text: "Attendance Data",
      linkTo: "/$leaid/records/attendance",
      hasPermission: hasRole("ATTNView"),
    },
    {
      text: "Add a Child",
      onClick: handleAddChild,
      hasPermission: hasRole("CHLDEdit"),
    },
    {
      text: "CSPPID Match Tool",
      hasPermission: hasRole("CHLDEdit"),
      onClick: handleMatchTool,
    },
    {
      text: "Make Local Data",
      linkTo: "/$leaid/records/local-data",
      hasPermission: hasRole("ADMIN") && leaContext.hasFeature("Internal"),
    },
  ];

  const menuItems: SidebarItemProps[] = [
    {
      icon: <FiHome />,
      text: "Home",
      linkTo: "/$leaid",
    },
    {
      icon: <FiList />,
      text: "View/Update Records",
      hasPermission: recordsSubItems.some((item) => item.hasPermission),
      subItems: recordsSubItems,
    },
    {
      icon: <FiUpload />,
      text: "File Upload",
      hasPermission: canFileUpload,
      linkTo: "/$leaid/upload",
    },
    {
      icon: <FiFileText />,
      text: (
        <span className="d-flex align-items-center">
          View Submissions
          {pendingCount > 0 && (
            <span
              style={{
                backgroundColor: "#f7b418",
                color: "#000",
                borderRadius: "50%",
                minWidth: "20px",
                height: "20px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "12px",
                fontWeight: 600,
                marginLeft: "8px",
                padding: pendingCount > 9 ? "0 6px" : "0",
                lineHeight: 1,
              }}
            >
              {pendingCount}
            </span>
          )}
        </span>
      ),
      hasPermission:
        canFileUpload || hasRole("CALPADSSync") || leaContext.isStateLevelAdmin,
      linkTo: "/$leaid/submissions",
    },
    {
      icon: <FiAlertTriangle />,
      text: "Discrepancies",
      hasPermission: hasRole("Extracts"),
      linkTo: "/$leaid/discrepancies",
    },
    {
      icon: <FiCheckCircle />,
      text: "Certification",
      hasPermission: hasRole(
        "Certifier",
        "ReportsEOY",
        "ReportsFall",
        "ReportsMonthly"
      ),
      subItems: [
        {
          text: "Current Cycle",
          linkTo: currentCycleId
            ? `/$leaid/certification-cycles/${currentCycleId}`
            : "/$leaid/certification-cycles",
        },
        {
          text: "List of Cycles",
          linkTo: "/$leaid/certification-cycles",
        },
      ],
    },
    {
      icon: <FiDownload />,
      text: "Data Exports",
      hasPermission: hasRole("Extracts"),
      linkTo: "/$leaid/exports",
    },
    {
      icon: <FiBarChart2 />,
      text: "Reports",
      hasPermission: hasRole("ReportsEOY", "ReportsFall", "ReportsMonthly"),
      linkTo: "/$leaid/reports",
    },
    {
      icon: <FiUsers />,
      text: "User Management",
      hasPermission: hasRole("ADMIN"),
      linkTo: "/$leaid/admin/users",
    },
    {
      icon: <FiMessageSquare />,
      text: "System Messages",
      hasPermission: hasRole("ADMIN") && isStateLevel,
      linkTo: "/$leaid/admin/system-messages",
    },
    {
      icon: <FiBarChart2 />,
      text: "State Level Reports",
      hasPermission: hasRole("ADMIN") && isStateLevel,
      linkTo: "/$leaid/admin/reports",
    },
    {
      icon: <FiGrid />,
      text: "Data Views",
      hasPermission:
        isAdminBuild() &&
        isStateLevel &&
        (hasRole("ADMIN") || hasRole("CALPADSSync")),
      subItems: [
        {
          text: "LEA Data View",
          linkTo: "/$leaid/admin/lea-data-view",
          hasPermission: isAdminBuild() && hasRole("ADMIN"),
        },
        {
          text: "CALPADS Sync Lookup",
          linkTo: "/$leaid/admin/calpads-sync",
          hasPermission: isAdminBuild() && hasRole("CALPADSSync"),
        },
        {
          text: "Child Data View",
          linkTo: "/$leaid/admin/child-data-view",
          hasPermission: isAdminBuild() && hasRole("ADMIN"),
        },
        {
          text: "State Level Data View",
          linkTo: "/$leaid/admin/state-level-data-view",
          hasPermission: isAdminBuild() && hasRole("ADMIN"),
        },
      ],
    },
    {
      icon: <FiSettings />,
      text: "Site Administration",
      hasPermission: isAdminBuild() && isStateLevel && hasRole("ADMIN"),
      subItems: [
        {
          text: "Feature Flags",
          linkTo: "/$leaid/admin/site-administration/feature-flags",
          hasPermission: isAdminBuild() && hasRole("ADMIN"),
        },
        {
          text: "Anomalies",
          linkTo: "/$leaid/admin/site-administration/anomalies",
          hasPermission: isAdminBuild() && hasRole("ADMIN"),
        },
      ],
    },
    {
      icon: <FiHelpCircle />,
      text: "Help",
      subItems: [
        {
          text: "CAPSDAC Support",
          linkTo: "https://www.cde.ca.gov/sp/cd/ci/capsdacsupportlanding.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("CAPSDAC Support"),
        },
        {
          text: "User Manual",
          linkTo: "https://www.cde.ca.gov/sp/cd/ci/capsdacchanges.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("User Manual"),
        },
        {
          text: "Error List",
          linkTo:
            "https://www.cde.ca.gov/sp/cd/ci/capsdacchanges.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("Error List"),
        },
        {
          text: "Data Certification Schedule",
          linkTo:
            "https://www.cde.ca.gov/sp/cd/ci/capsdacsubmissonschedule.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("Data Submission Schedule"),
        },
        {
          text: "Batch File Template Download",
          linkTo: "https://www.cde.ca.gov/sp/cd/ci/capsdacchanges.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("Batch File Template Download"),
        },
        {
          text: "Webinars & Office Hours",
          linkTo: "https://www.cde.ca.gov/sp/cd/ci/capsdacwebinars.asp",
          isExternal: true,
          title: getExternalLinkNewTabTitle("Upcoming Training Schedule"),
        },
        {
          text: "Service Requests",
          linkTo: "https://caeducation.service-now.com/capsdac",
          isExternal: true,
          title: getExternalLinkNewTabTitle("Service Requests"),
        },
      ],
    },
  ];

  return (
    <div className="lea-sidebar d-flex flex-column h-100">
      <div className="sidebar-menu-scroll flex-grow-1 overflow-auto">
        <SidebarMenu menuItems={menuItems} />
      </div>

      <div className="p-3 sidebar-quick-search">
        <QuickSearch />
      </div>
    </div>
  );
}

function getExternalLinkNewTabTitle(linkName: string) {
  return linkName
    ? `External link to ${linkName} opens in a new tab`
    : "External link opens in a new tab";
}
